import { Box, Card, CircularProgress, Grid, Typography } from "@mui/material";

import { BudgetDashboardSummary } from "../../../types/dashboard";
import { formatDelta, formatPercent, formatShortDate } from "../utils";

interface AdoptionKpisProps {
  summary?: BudgetDashboardSummary;
  isLoading: boolean;
}

const KPI_COLORS = ["#1A5CB0", "#16A34A", "#DC2626"];

const AdoptionDelta = ({ summary }: { summary: BudgetDashboardSummary }) => {
  const { adoptionDelta, previousPeriod } = summary;

  if (adoptionDelta === null || !previousPeriod) {
    return (
      <Typography
        fontSize={11.5}
        fontStyle="italic"
        color="rgba(255,255,255,.72)"
        mt={1}
      >
        sem período anterior para comparar
      </Typography>
    );
  }

  const arrow = adoptionDelta > 0 ? "▲" : adoptionDelta < 0 ? "▼" : "▬";
  const color =
    adoptionDelta > 0 ? "#C7F9D8" : adoptionDelta < 0 ? "#FFD0CF" : "#E9ECF3";

  return (
    <Box mt="6px" display="flex" flexDirection="column" gap="2px">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          width: "fit-content",
          px: "9px",
          py: "1px",
          borderRadius: "20px",
          backgroundColor: "rgba(255,255,255,.16)",
          fontSize: 13.5,
          fontWeight: 800,
          color,
        }}
      >
        {arrow} {formatDelta(adoptionDelta)}
      </Box>
      <Typography fontSize={10.5} color="rgba(255,255,255,.78)">
        vs. período anterior ({formatShortDate(previousPeriod.startDate)}–
        {formatShortDate(previousPeriod.endDate)}) ·{" "}
        {formatPercent(previousPeriod.adoptionRate)}
      </Typography>
    </Box>
  );
};

export const AdoptionKpis = ({ summary, isLoading }: AdoptionKpisProps) => {
  const totalProjects = summary?.totalProjects ?? 0;
  const adoptionRate = summary?.adoptionRate ?? 0;

  const shareOf = (value: number) =>
    totalProjects
      ? `${Math.round((value / totalProjects) * 100)}% do total`
      : "";

  const counters = [
    {
      label: "Total de Projetos",
      value: summary?.totalProjects ?? 0,
      caption: summary
        ? `${summary.totalBudgets} orçamento(s) vinculado(s)`
        : "",
    },
    {
      label: "Com Orçamento",
      value: summary?.withBudget ?? 0,
      caption: shareOf(summary?.withBudget ?? 0),
    },
    {
      label: "Sem Orçamento",
      value: summary?.withoutBudget ?? 0,
      caption: shareOf(summary?.withoutBudget ?? 0),
    },
  ];

  return (
    <Grid container spacing={2} mb={2.5}>
      {counters.map((counter, index) => (
        <Grid item xs={12} md={3} key={counter.label}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              boxSizing: "border-box",
              p: "18px 20px",
              border: "1px solid #E4E7EC",
              borderRadius: "12px",
            }}
          >
            <Typography fontSize={13} color="#6B7686" mb={1}>
              {counter.label}
            </Typography>
            <Typography
              fontSize={30}
              fontWeight={700}
              lineHeight={1}
              color={KPI_COLORS[index]}
            >
              {isLoading ? <CircularProgress size={24} /> : counter.value}
            </Typography>
            <Typography fontSize={14} fontWeight={700} color="#6B7686" mt={1}>
              {isLoading ? "" : counter.caption}
            </Typography>
          </Card>
        </Grid>
      ))}

      <Grid item xs={12} md={3}>
        <Card
          elevation={0}
          sx={{
            height: "100%",
            boxSizing: "border-box",
            p: "18px 20px",
            borderRadius: "12px",
            background: "linear-gradient(135deg,#1A5CB0,#5B3FC9)",
            color: "#FFFFFF",
          }}
        >
          <Typography fontSize={13} color="rgba(255,255,255,.85)" mb={1}>
            % Adoção de Orçamento
          </Typography>
          <Typography fontSize={34} fontWeight={700} lineHeight={1}>
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
            ) : (
              formatPercent(adoptionRate)
            )}
          </Typography>

          {!isLoading && summary && <AdoptionDelta summary={summary} />}

          <Box
            sx={{
              mt: 1.5,
              height: 6,
              borderRadius: "4px",
              backgroundColor: "rgba(255,255,255,.25)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${Math.min(100, adoptionRate)}%`,
                height: "100%",
                borderRadius: "4px",
                backgroundColor: "#FFFFFF",
                transition: "width .4s ease",
              }}
            />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};
